import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import moment from 'moment';

import Modal from './modal';

import {
  Container,
  ReleaseButton,
  Text,
  SegmentName,
  ButtonContainer,
  SegmentContainer,
} from './styles';
import { CREATE_USER_INTERVIEWS_PROJECT, LAUNCH_USER_INTERVIEWS_PROJECT } from './graphql';
import TestTypes from '../../../../../core/constants/testTypes';
import { mapSegmentsUserInterviewIds } from './utils/mapSegmentsUserInterviewIds';

const Integrations = (props) => {
  const { project, refetch } = props;

  const [showDraftModal, setShowDraftModal] = useState({});
  const [showLaunchModal, setShowLaunchModal] = useState({});
  const [segmentsUserInterview, setSegmentsUserInterview] = useState({});

  const [createUserInterviewsProject, { loading: draftLoading }] = useMutation(CREATE_USER_INTERVIEWS_PROJECT, {
    onCompleted: () => {
      refetch();
    },
  });
  const [launchUserInterviewsProject, { loading: launchLoading }] = useMutation(LAUNCH_USER_INTERVIEWS_PROJECT, {
    onCompleted: () => {
      refetch();
    },
  });

  const handleCloseDraftModal = (id) => () => {
    setShowDraftModal({ [id]: false });
  };

  const handleCloseLaunchModal = (id) => () => {
    setShowLaunchModal({ [id]: false });
  };

  const handleOpenDraftButton = (id) => () => {
    setShowDraftModal({ [id]: true });
  };

  const handleOpenLaunchButton = (id) => () => {
    setShowLaunchModal({ [id]: true });
  };

  const handleDraftConfirm = (segmentId) => async () => {
    const response = await createUserInterviewsProject({
      variables: {
        segmentId,
      },
    });
    const userInterviewsProjectId = response?.data?.createUserInterviewsProject?.segment?.userInterviewsProjectId || null;
    const userInterviewsProjectCreatedAt = response?.data?.createUserInterviewsProject?.segment?.userInterviewsProjectCreatedAt || null;
    const updatedSegmentsUserInterviewIds = { ...segmentsUserInterview };
    updatedSegmentsUserInterviewIds[segmentId] = {
      userInterviewsProjectId,
      userInterviewsProjectCreatedAt,
    };
    setSegmentsUserInterview(updatedSegmentsUserInterviewIds);
    setShowDraftModal({ [segmentId]: false });
  };

  const handleLaunchConfirm = (segmentId) => async () => {
    const response = await launchUserInterviewsProject({
      variables: {
        segmentId,
      },
    });
    const userInterviewsProjectLaunch = response?.data?.launchUserInterviewsProject?.segment?.userInterviewsProjectLaunch || null;
    const userInterviewsProjectLaunchAt = response?.data?.launchUserInterviewsProject?.segment?.userInterviewsProjectLaunchAt || null;
    const updatedSegmentsUserInterviewIds = { ...segmentsUserInterview };
    updatedSegmentsUserInterviewIds[segmentId] = {
      ...updatedSegmentsUserInterviewIds[segmentId],
      userInterviewsProjectLaunch,
      userInterviewsProjectLaunchAt,
    };
    setSegmentsUserInterview(updatedSegmentsUserInterviewIds);
    setShowLaunchModal({ [segmentId]: false });
  };

  useEffect(() => {
    if (draftLoading) {
      handleCloseDraftModal(false);
    }
  }, [draftLoading]);

  useEffect(() => {
    if (launchLoading) {
      handleCloseLaunchModal(false);
    }
  }, [launchLoading]);

  useEffect(() => {
    const mappedSegmentsUserInterviewIds = mapSegmentsUserInterviewIds(project);
    setSegmentsUserInterview(mappedSegmentsUserInterviewIds);
  }, [project]);

  // unmoderated, card sort, tree test and surveys are allowed for user interviews
  const projectTypesAllowedForUserInterviews = [TestTypes.UNMODERATED, TestTypes.CARD_SORT, TestTypes.TREE_TEST, TestTypes.SURVEY];
  const isAllowedForUserInterviews = projectTypesAllowedForUserInterviews.includes(project.testType);

  return (
    <Container>
      {project.segments.map(({ id, name }) => {
        const {
          userInterviewsProjectId,
          userInterviewsProjectCreatedAt,
          userInterviewsProjectLaunch,
          userInterviewsProjectLaunchAt,
        } = segmentsUserInterview[id] || {};

        return (
          <SegmentContainer key={id}>
            {project.participantsSource === 'playbook' && (
              <SegmentName>{name}</SegmentName>
            )}

            {showDraftModal[id] && (
              <Modal
                onConfirm={handleDraftConfirm(id)}
                onClose={handleCloseDraftModal(id)}
                loading={draftLoading}
                open={showDraftModal[id]}
                text="Are you sure you want to create a draft on User Interviews?"
                confirmText="Yes, create draft"
                cancelText="Cancel"
              />
            )}

            {showLaunchModal[id] && (
              <Modal
                onConfirm={handleLaunchConfirm(id)}
                onClose={handleCloseLaunchModal(id)}
                loading={launchLoading}
                open={showLaunchModal[id]}
                text="Are you sure you want to launch this on User Interviews?"
                confirmText="Yes, launch it"
                cancelText="Cancel"
              />
            )}

            <ButtonContainer>
              <ReleaseButton
                onClick={handleOpenDraftButton(id)}
              >
                Create Draft on User Interviews
              </ReleaseButton>
              {(!!userInterviewsProjectId && !!userInterviewsProjectCreatedAt && isAllowedForUserInterviews) && (
                <ReleaseButton
                  onClick={handleOpenLaunchButton(id)}
                >
                  Launch on User Interviews
                </ReleaseButton>
              )}
            </ButtonContainer>
            {(!!userInterviewsProjectId && !!userInterviewsProjectCreatedAt && !userInterviewsProjectLaunch) && (
              <>
                <Text.Success>Project Draft Created</Text.Success>
                <Text>
                  {`A project has been created at ${moment(userInterviewsProjectCreatedAt).format('MMM DD YYYY hh:mm A')}`}
                </Text>
              </>
            )}
            {(!!userInterviewsProjectId
              && !!userInterviewsProjectCreatedAt
              && !!userInterviewsProjectLaunch
              && isAllowedForUserInterviews
            )
              && (
                <>
                  <Text.Success>Project Launched</Text.Success>
                  <Text>
                    {`A project has been launched at ${moment(userInterviewsProjectLaunchAt).format('MMM DD YYYY hh:mm A')}`}
                  </Text>
                </>
              )}
            {(!userInterviewsProjectId || !userInterviewsProjectCreatedAt) && (
              <Text.Error>Project not Launched</Text.Error>
            )}
          </SegmentContainer>
        );
      })}
    </Container>
  );
};

Integrations.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string,
    testType: PropTypes.string,
    participantsSource: PropTypes.string,
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
  }).isRequired,
  refetch: PropTypes.func.isRequired,
};

export default Integrations;
